import FormattedMessage from 'I18n/components/FormattedMessage';
import UIExpandableSection from 'UIComponents/accordion/UIExpandableSection';
import UIOptimisticNanoProgress from 'UIComponents/progress/UIOptimisticNanoProgress';
import { useAllExtensionDefinitions } from 'automation-ui-action-features/actions/extension/extensionQueries';
import { AUTOMATION_EXTENSION } from 'automation-ui-action-features/constants/ActionTypes';
import { createActionCardCounters, getActionHeaderTitle } from 'automation-ui-action-features/records/recordsUtils';
import { useGetConversionMetrics } from 'automation-ui-canvas/containers/enrollment-conversion/panel/hooks/enrollmentConversionHooks';
import EnrollmentConversionPanelBranchTableContent from 'automation-ui-canvas/containers/enrollment-conversion/panel/table/EnrollmentConversionPanelBranchTableContent';
import EnrollmentConversionPanelCompletedContent from 'automation-ui-canvas/containers/enrollment-conversion/panel/table/EnrollmentConversionPanelCompletedContent';
import EnrollmentConversionPanelSummaryContent from 'automation-ui-canvas/containers/enrollment-conversion/panel/table/EnrollmentConversionPanelSummaryContent';
import { getObjectTypeId, getSourceApp } from 'automation-ui-canvas/flow/records/Flow/flowUtils';
import { getMetricsUrl } from 'automation-ui-canvas/utils/urls';
import { useGetIsUngated } from 'automation-ui-core/hooks/authHooks';
import { useApplicationHistoryFilters } from 'automation-ui-management/hooks/filterHooks';
import { getTimestampsFromHistoryFilters } from 'automation-ui-management/records/ApplicationHistoryFilters/ApplicationHistoryFilterUtils';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import { useTrackEvent } from 'automation-ui-usage-tracking/usageTrackingHooks';
import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const EnrollmentConversionTableContent = ({
  currentVersionGroupFlow,
  isReportingEnabled
}) => {
  var _allExtensionsData$al;
  const isUngated = useGetIsUngated();
  const history = useHistory();
  const trackEvent = useTrackEvent();
  const [isSummarySectionOpen, setIsSummarySectionOpen] = useState(true);
  const actions = currentVersionGroupFlow.getActions();
  const {
    search
  } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const {
    enrollmentConversionData,
    isConversionDataLoading
  } = useGetConversionMetrics(currentVersionGroupFlow, isReportingEnabled);
  const isVersionAvailable = searchParams.has('version');
  const activeActionId = searchParams.get('activeActionId');

  // Open the summary section if the activeActionId is present in the URL
  useEffect(() => {
    if (activeActionId && !isSummarySectionOpen) {
      setIsSummarySectionOpen(true);
    }
  }, [activeActionId, isSummarySectionOpen]);
  const flowObjectTypeId = getObjectTypeId(currentVersionGroupFlow);
  const uniqueExtensionActionTypeIds = currentVersionGroupFlow ? [...new Set(currentVersionGroupFlow.actions.toArray().filter(actionInFlow => actionInFlow.actionType === AUTOMATION_EXTENSION).map(actionInFlow => actionInFlow.actionTypeId))] : [];
  const {
    data: allExtensionsData,
    loading: isExtensionsDataLoading
  } = useAllExtensionDefinitions({
    objectType: flowObjectTypeId,
    createdSourceApp: getSourceApp(currentVersionGroupFlow) || undefined,
    actionTypeIdsToInclude: uniqueExtensionActionTypeIds
  });
  const extensionDefinitions = (_allExtensionsData$al = allExtensionsData === null || allExtensionsData === void 0 ? void 0 : allExtensionsData.allExtensionDefinitions) !== null && _allExtensionsData$al !== void 0 ? _allExtensionsData$al : {};
  const getHeaderTitle = getActionHeaderTitle({
    flowActions: actions,
    firstActionId: currentVersionGroupFlow.firstActionId,
    extensionDefinitions,
    isUngated
  });
  const actionCardCounters = createActionCardCounters(currentVersionGroupFlow.getActions(), currentVersionGroupFlow.firstActionId);
  const {
    filters
  } = useApplicationHistoryFilters();
  const timeStamps = getTimestampsFromHistoryFilters(filters);
  if (isExtensionsDataLoading || isConversionDataLoading || !isVersionAvailable || !timeStamps) {
    return /*#__PURE__*/_jsx(UIOptimisticNanoProgress, {});
  }
  const orderedActions = actions.toArray().sort((aComparator, bComparator) => actionCardCounters[aComparator.actionId] - actionCardCounters[bComparator.actionId]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(UIExpandableSection, {
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation.enrollmentConversion.panel.sections.summary"
      }),
      use: "enclosed",
      open: isSummarySectionOpen,
      contentClassName: "p-all-0",
      onOpenChange: ({
        target: {
          value
        }
      }) => {
        trackEvent(Trackables.AGGREGATE_PERFORMANCE_SUMMARY_SECTION_TOGGLE, {
          toggleType: value ? 'Expand' : 'Collapse'
        });
        setIsSummarySectionOpen(value);
        searchParams.delete('activeActionId');
        history.replace({
          pathname: getMetricsUrl({
            flowId: currentVersionGroupFlow.flowId
          }),
          search: searchParams.toString()
        });
      },
      children: /*#__PURE__*/_jsx(EnrollmentConversionPanelSummaryContent, {
        enrollmentConversionData: enrollmentConversionData,
        orderedActions: orderedActions,
        isReportingEnabled: isReportingEnabled,
        getHeaderTitle: getHeaderTitle,
        currentVersionGroupFlow: currentVersionGroupFlow
      })
    }), /*#__PURE__*/_jsxs(UIExpandableSection, {
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation.enrollmentConversion.panel.sections.conversionMetrics"
      }),
      use: "enclosed",
      defaultOpen: true,
      contentClassName: "p-all-0",
      onOpenChange: ({
        target: {
          value
        }
      }) => {
        trackEvent(Trackables.AGGREGATE_PERFORMANCE_CONVERSION_METRICS_SECTION_TOGGLE, {
          toggleType: value ? 'Expand' : 'Collapse'
        });
      },
      children: [/*#__PURE__*/_jsx(EnrollmentConversionPanelBranchTableContent, {
        currentVersionGroupFlow: currentVersionGroupFlow,
        orderedActions: orderedActions,
        enrollmentConversionData: enrollmentConversionData,
        getHeaderTitle: getHeaderTitle,
        isReportingEnabled: isReportingEnabled
      }), /*#__PURE__*/_jsx(EnrollmentConversionPanelCompletedContent, {
        enrollmentConversionData: enrollmentConversionData,
        flow: currentVersionGroupFlow,
        isReportingEnabled: isReportingEnabled
      })]
    })]
  });
};
export default EnrollmentConversionTableContent;